import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tv } from 'lucide-react';

function getPublicApiBase() {
  const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (configuredBaseUrl) {
    const configuredUrl = new URL(configuredBaseUrl);
    return configuredUrl.origin.replace(/\/$/, '');
  }

  const apiUrl = new URL(window.location.origin);
  apiUrl.port = '3000';
  return apiUrl.toString().replace(/\/$/, '');
}

export function LiveEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [loaded, setLoaded] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const liveUrl = useMemo(() => {
    if (!eventId) return '';
    return `${getPublicApiBase()}/api/v1/public/events/${eventId}/workspace/live`;
  }, [eventId]);

  useEffect(() => {
    let cancelled = false;

    async function verifyLivePage() {
      if (!liveUrl) {
        setChecking(false);
        setError('This event page is not available.');
        return;
      }

      setChecking(true);
      setLoaded(false);
      setError(null);

      try {
        const response = await fetch(liveUrl, {
          method: 'GET',
          headers: {
            Accept: 'text/html,application/json',
          },
        });

        if (!response.ok) {
          let message = 'This event page is not available.';

          try {
            const payload = await response.json();
            message = payload?.error?.message || message;
          } catch {
            // Ignore non-JSON error bodies and use the default message.
          }

          if (!cancelled) {
            setError(message);
            setChecking(false);
          }
          return;
        }

        if (!cancelled) {
          setChecking(false);
        }
      } catch {
        if (!cancelled) {
          setError('Could not reach the live event page.');
          setChecking(false);
        }
      }
    }

    verifyLivePage();

    return () => {
      cancelled = true;
    };
  }, [liveUrl]);

  if (!eventId || !liveUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-1">
        <div className="text-center max-w-md px-6">
          <Tv className="mx-auto mb-4 h-10 w-10 text-neutral-7" aria-hidden="true" />
          <h1 className="text-xl font-bold text-neutral-10 mb-2">Event not found</h1>
          <p className="text-neutral-7 text-sm">This event page is not available.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-1">
        <div className="text-center max-w-md px-6">
          <Tv className="mx-auto mb-4 h-10 w-10 text-neutral-7" aria-hidden="true" />
          <h1 className="text-xl font-bold text-neutral-10 mb-2">Live page unavailable</h1>
          <p className="text-neutral-7 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-neutral-1">
      {!checking ? (
        <iframe
          src={liveUrl}
          title="Live Event Page"
          className="h-screen w-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms"
          onLoad={() => setLoaded(true)}
        />
      ) : null}

      {checking || !loaded ? (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-1">
          <ContentSkeleton label={checking ? 'Checking live event' : 'Loading event'} variant="viewer" className="max-w-7xl px-6" />
        </div>
      ) : null}
    </div>
  );
}

export default LiveEventPage;
